import { SxProps, Theme } from "@mui/material/styles";
import { gridClasses } from "@mui/x-data-grid";
import { lightScrollbarStyle } from "@/styles/lightScrollbarStyle";

/**
 * Default styles for DataTable component.
 * Provides consistent styling across all table instances.
 */
export const dataTableStyles: SxProps<Theme> = {
  // Remove focus outlines
  [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
    outline: "none",
  },
  [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
    {
      outline: "none",
    },

  // Hide column separators (except resizable ones)
  [`& .${gridClasses.columnSeparator}`]: {
    [`&:not(.${gridClasses["columnSeparator--resizable"]})`]: {
      display: "none",
    },
  },

  // Column header styling
  "& .MuiDataGrid-columnHeader": {
    borderBottom: "none !important",
  },
  "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
    "& .MuiIconButton-root": {
      padding: 0,
    },
    "& .MuiTouchRipple-root": {
      display: "none",
    },
  },
  "& .MuiDataGrid-columnHeaders": {
    color: "#3a3a3a",
    fontSize: "12px",
    fontWeight: "bold",
  },

  // Remove borders
  "&, [class^=MuiDataGrid]": { border: "none" },

  // Cell styling
  "& .MuiDataGrid-cell": {
    display: "flex",
    alignItems: "center",
    borderBottom: "none",
  },

  // Hide fillers
  "& .MuiDataGrid-filler": {
    display: "none",
  },
  ".MuiDataGrid-scrollbarFiller": {
    display: "none",
  },

  // Scrollbar styling
  "& .MuiDataGrid-scrollbar--vertical": {
    ...lightScrollbarStyle,
  },
  "& .MuiDataGrid-scrollbar--horizontal": {
    ...lightScrollbarStyle,
  },

  // Row styling
  "& .MuiDataGrid-row": {
    cursor: "pointer",
    textDecoration: "none",
    color: "inherit",
    "&:hover": {
      backgroundColor: "action.hover",
    },
  },
};

/**
 * Merge custom sx styles with default DataTable styles.
 * Custom styles take precedence over defaults.
 */
export function mergeDataTableStyles(
  customSx?: SxProps<Theme>
): SxProps<Theme> {
  if (!customSx) {
    return dataTableStyles;
  }

  // Handle array of sx objects
  if (Array.isArray(customSx)) {
    return [dataTableStyles, ...customSx] as SxProps<Theme>;
  }

  return [dataTableStyles, customSx] as SxProps<Theme>;
}
